import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Linkedin, Twitter, Facebook, Youtube } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";

export function Footer() {
  const { t, language } = useLanguage();

  // Translated platform links
  const platformLinks = [
    { label: t('footer.platform.about'), path: "/about" },
    { label: t('footer.platform.howItWorks'), path: "/how-it-works" },
    { label: t('footer.platform.experts'), path: "/experts" },
    { label: t('footer.platform.achievements'), path: "/achievements" },
  ];

  // Translated investor links
  const investorLinks = [
    { label: t('footer.investors.investmentOpportunities'), path: "/projects" },
    { label: t('footer.investors.investor'), path: "/investor" },
    { label: t('footer.investors.partners'), path: "/partners" },
    { label: t('footer.investors.financialAlternatives'), path: "/financial-alternatives" },
    { label: t('footer.investors.sixMonthProgram'), path: "/program" },
  ];

  return (
    <footer className="bg-sidebar text-sidebar-foreground" dir={language === "ar" ? "rtl" : "ltr"}>
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 group">
              <div className="w-10 h-10 bg-hero-gradient rounded-lg flex items-center justify-center transition-transform group-hover:scale-110 group-hover:rotate-3">
                <span className="text-primary-foreground font-bold text-lg">K+</span>
              </div>
              <span className="font-bold text-lg">knowvest.plus</span>
            </div>
            <p className="text-sidebar-foreground/70 text-sm leading-relaxed">
              {t('footer.brandDescription')}
            </p>
            <div className="flex gap-3">
              <a href="https://www.linkedin.com/in/rahmani-mustapha-57100184/?skipRedirect=true" className="w-10 h-10 rounded-full bg-sidebar-accent flex items-center justify-center hover:bg-sidebar-primary transition-all duration-300 hover:scale-110 hover:-translate-y-1">
                <Linkedin className="w-5 h-5" />
              </a>
              <a href="https://www.youtube.com/@innovpluscompany" className="w-10 h-10 rounded-full bg-sidebar-accent flex items-center justify-center hover:bg-sidebar-primary transition-all duration-300 hover:scale-110 hover:-translate-y-1">
                <Youtube className="w-5 h-5" />
              </a>
              <a href="https://web.facebook.com/profile.php?id=61555076450376&_rdc=1&_rdr" className="w-10 h-10 rounded-full bg-sidebar-accent flex items-center justify-center hover:bg-sidebar-primary transition-all duration-300 hover:scale-110 hover:-translate-y-1">
                <Facebook className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Platform Links */}
          <div>
            <h3 className="font-bold text-lg mb-4">{t('footer.platform.title')}</h3>
            <ul className="space-y-3">
              {platformLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sidebar-foreground/70 hover:text-sidebar-foreground transition-all duration-200 text-sm hover:translate-x-1 inline-block link-underline"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Investor Links */}
          <div>
            <h3 className="font-bold text-lg mb-4">{t('footer.investors.title')}</h3>
            <ul className="space-y-3">
              {investorLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sidebar-foreground/70 hover:text-sidebar-foreground transition-all duration-200 text-sm hover:translate-x-1 inline-block link-underline"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-bold text-lg mb-4">{t('footer.contact.title')}</h3>
            <ul className="space-y-3">
              <li className="flex items-center gap-3 text-sm text-sidebar-foreground/70 hover:text-sidebar-foreground transition-colors group">
                <Mail className="w-5 h-5 text-sidebar-primary group-hover:scale-110 transition-transform" />
                <span>{t('footer.contact.email')}</span>
              </li>
              <li className="flex items-center gap-3 text-sm text-sidebar-foreground/70 hover:text-sidebar-foreground transition-colors group">
                <Phone className="w-5 h-5 text-sidebar-primary group-hover:scale-110 transition-transform" />
                <span dir="ltr">{t('footer.contact.phone')}</span>
              </li>
              <li className="flex items-center gap-3 text-sm text-sidebar-foreground/70 hover:text-sidebar-foreground transition-colors group">
                <MapPin className="w-5 h-5 text-sidebar-primary group-hover:scale-110 transition-transform" />
                <span>{t('footer.contact.address')}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-sidebar-border mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-sidebar-foreground/60">
            {t('footer.copyright')}
          </p>
          <div className="flex gap-6 text-sm text-sidebar-foreground/60">
            <Link to="/privacy" className="hover:text-sidebar-foreground transition-colors link-underline">
              {t('footer.privacyPolicy')}
            </Link>
            <Link to="/terms" className="hover:text-sidebar-foreground transition-colors link-underline">
              {t('footer.terms')}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}